import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { getAppUrl, withRoutePrefix } from '../config/routePrefix';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const { instance, accounts, inProgress } = useMsal();
    const [token, setToken] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const isE2EAuthBypass = process.env.NODE_ENV === 'development' && process.env.REACT_APP_E2E_AUTH_BYPASS === 'true';

    const isJobDetailPath = (path = "") => /^\/\d+\/(generalInfo|salesInfo|mechInfo|elecInfo|changeLog|jobQa|additionalItems|forms|changeOrders)$/.test(path);

    // New effect to handle redirect URL storage and handling
    useEffect(() => {
        if (isE2EAuthBypass) {
            setToken('e2e-auth-bypass');
            setIsAuthenticated(true);
            setIsInitialized(true);
            return;
        }

        //console.log("Accounts:", accounts);
        //console.log("inProgress:", inProgress);

        // Store redirect URL only for unauthenticated users (pre-login intent), never during active sessions
        if (accounts.length === 0 && window.location.pathname !== "/") {
            //console.log("Storing redirect URL in localStorage:", currentUrl);
            localStorage.setItem("postAuthRedirectUrl", window.location.pathname);
        }

        // Prevent stale deep-link redirects from surviving future authenticated visits
        if (accounts.length > 0 && window.location.pathname === "/") {
            const staleRedirect = localStorage.getItem("postAuthRedirectUrl");
            if (staleRedirect && isJobDetailPath(staleRedirect)) {
                localStorage.removeItem("postAuthRedirectUrl");
            }
        }

        const reAcquireToken = async (account) => {
            //console.log("Attempting to acquire token...");
            try {
                const request = {
                    scopes: ["user.read"],
                    account: account,
                };
                const response = await instance.acquireTokenSilent(request);
                setToken(response.accessToken);
                setIsAuthenticated(true);
                //console.log("Token acquired successfully:", response);
            } catch (error) {
                console.error("Error acquiring token silently: ", error);
                setIsAuthenticated(false);
            }
        };

        const initializeMsal = async () => {
            //console.log("Initializing MSAL...");
            try {
                const response = await instance.handleRedirectPromise();
                if (response) {
                    //console.log("Login successful after redirect.");
                    await reAcquireToken(response.account);

                    // Retrieve the stored redirect URL from localStorage
                    const redirectUrl = localStorage.getItem('postAuthRedirectUrl');
                    const rootUrl = window.location.origin;

                    if (redirectUrl && redirectUrl !== rootUrl) {
                        // If a redirect URL is stored, redirect to that URL
                        //console.log("Redirecting to stored URL:", storedRedirectUri);
                        localStorage.removeItem("postAuthRedirectUrl");
                        window.location.href = redirectUrl;
                    } else {
                        // If no stored redirect URL, default to the root
                        window.location.href = withRoutePrefix("/");
                    }
                } else {
                    const activeAccount = instance.getActiveAccount() || accounts[0];
                    if (activeAccount) {
                        //console.log("Active account found.");
                        await reAcquireToken(activeAccount);
                    }
                }
            } catch (error) {
                console.error("MSAL initialization error: ", error);
            } finally {
                setIsInitialized(true);
                //console.log("MSAL initialization complete.");
            }
        };

        if (inProgress === "none") {
            //console.log("No login in progress, initializing MSAL...");
            initializeMsal();
        } else {
            //console.log("Login in progress, waiting...");
        }
    }, [instance, accounts, inProgress, isE2EAuthBypass]);

    const login = async () => {
        //console.log("Logging in...");

        // Store the current path in localStorage before initiating the login
        // if (window.location.pathname !== "/") {
        //     localStorage.setItem("postAuthRedirectUrl", window.location.pathname);
        //     console.log("login function:", window.location.pathname)
        // }

        try {
            await instance.loginRedirect({
                scopes: ["user.read"],
                redirectUri: getAppUrl("/"),  // This is where the redirect will happen post-login
            });
        } catch (e) {
            console.error("Error during login:", e);
        }
        localStorage.removeItem("postAuthRedirectUrl")
    };

    const logout = async () => {
        //console.log("Logging out...");
        try {
            await instance.logoutRedirect({
                postLogoutRedirectUri: getAppUrl("/"),
            });
            setToken(null);
            setIsAuthenticated(false);
            localStorage.removeItem("postAuthRedirectUrl"); // Clear stored redirect URL on logout
        } catch (e) {
            console.error("Error during logout:", e);
        }
    };

    if (!isInitialized) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider value={{ token, login, logout, isAuthenticated }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
